import isNodeList from './IsNodeList'

class Helpers {
    constructor(element) {
        // Ensure whatever element is passed into the
        // constructor is an array, as methods loop
        // over every element using `forEach`.
        if (Array.isArray(element)) {
            this.element = element
        } else if (!isNodeList(element)) {
            this.element = [element]
        } else {
            this.element = Array.from(element)
        }
    }

    /**
     * Get closest element
     *
     * @param  {HTMLElement|HTMLCollection|NodeList} selector Name of closest element to get
     * @return {HTMLElement|HTMLCollection|NodeList}          Closest element
     */
    getClosest(selector) {
        this.element.forEach((element) => {
            let currentElement = element

            while (currentElement) {
                if (typeof selector === 'string') {
                    if (currentElement.matches(selector)) {
                        this.closestElement = currentElement
                        return
                    }
                } else if (currentElement === selector) {
                    this.closestElement = currentElement
                    return
                }

                currentElement = currentElement.parentElement
            }
        })

        return this.closestElement
    }

    getNext(selector) {
        this.element.forEach((element) => {
            let currentElement = element

            while (currentElement = currentElement.nextElementSibling) {
                if (currentElement.matches(selector)) {
                    this.nextElement = currentElement
                    return
                }
            }
        })

        return this.nextElement
    }

    getPrevious(selector) {
        this.element.forEach((element) => {
            let currentElement = element

            while (currentElement = currentElement.previousElementSibling) {
                if (currentElement.matches(selector)) {
                    this.previousElement = currentElement
                    return
                }
            }
        })

        return this.previousElement
    }

    getParents(elem, selector) {
        // credit to Chris Ferdinandi: https://gomakethings.com/climbing-up-and-down-the-dom-tree-with-vanilla-javascript/#getting-all-matches-up-the-tree
        // Element.matches() polyfill
        if (!Element.prototype.matches) {
            Element.prototype.matches =
                Element.prototype.matchesSelector ||
                Element.prototype.mozMatchesSelector ||
                Element.prototype.msMatchesSelector ||
                Element.prototype.oMatchesSelector ||
                Element.prototype.webkitMatchesSelector ||
                function (s) {
                    var matches = (this.document || this.ownerDocument).querySelectorAll(s),
                        i = matches.length;
                    while (--i >= 0 && matches.item(i) !== this) { }
                    return i > -1;
                };
        }

        // Setup parents array
        var parents = [];

        // Get matching parent elements
        for (; elem && elem !== document; elem = elem.parentNode) {

            // Add matching parents to array
            if (selector) {
                if (elem.matches(selector)) {
                    parents.push(elem);
                }
            } else {
                parents.push(elem);
            }

        }

        return parents;

    };

    /**
     * Set multiple element attributes
     *
     * @param  {Object} attributes Attributes and values to set
     * @return {Object}
     */
    setAttributes(attributes) {
        this.element.forEach((element) => {
            Object.keys(attributes).forEach((key) => {
                const attrValue = attributes[key]
                if (attrValue === null) {
                    element.removeAttribute(key)
                } else {
                    element.setAttribute(key, attrValue)
                }
            })
        })

        return this
    }

    /**
     * Remove multiple element attributes
     *
     * @param  {Array} attributes Array of objects to remove
     * @return {Object}
     */
    removeAttributes(attributes) {
        this.element.forEach((element) => {
            attributes.forEach(function (key, index) {
                element.removeAttribute(key)
            })
        })

        return this
    }


    /**
     * Add class to clement
     *
     * @param  {String} className Class name to add
     * @return {Object}
     */
    addClass(...className) {
        this.element.forEach(element => element.classList.add(...className))

        return this
    }


    /**
     * Remove class from element
     *
     * @param  {String} className Class name to remove
     * @return {Object}
     */
    removeClass(...className) {
        this.element.forEach(element => element.classList.remove(...className))

        return this
    }


    /**
     * Toggle class on element
     *
     * @param  {String} className Class name to toggle
     * @return {Object}
     */
    toggleClass(...className) {
        this.element.forEach(element => element.classList.toggle(...className))

        return this
    }


    /**
     * Check if element has class
     *
     * @param  {String}  className Class name to check
     * @return {Boolean}           Whether element has class or not
     */
    hasClass(className) {
        return this.element.filter(element => element.classList.contains(className)).length
    }


    /**
     * Toggle element true/false attribute
     *
     * @param  {String} attribute Attribute to toggle
     * @return {Object}
     */
    toggleAttribute(attribute) {
        this.element.forEach((element) => {
            if (element.getAttribute(attribute) === 'true') {
                element.setAttribute(attribute, 'false')
            } else {
                element.setAttribute(attribute, 'true')
            }
        })

        return this
    }


    /**
     *  Create event listener
     *
     * @param  {Function} callback                         Event listener callback function
     * @param  {String}   [eventListener='click']          Desktop event
     * @return {Object}
     */
    eventListener(callback, eventListener = 'click') {
        this.element.forEach((element) => {
            if (element) {
                element.addEventListener(eventListener, callback, false)
            }
        })

        return this
    }

    delegateEventListener(selector, handler, eventListener = 'click') {
        this.element.forEach((element) => {
            if (element) {
                element.addEventListener(eventListener, function (event) {
                    var listeningTarget = new Helpers(event.target).getClosest(selector)
                    if (listeningTarget) {
                        handler.call(listeningTarget, event)
                    }
                });
            }
        })

        return this
    }

    /**
     * Remove item from array
     *
     * @param  {HTMLElement|HTMLCollection|NodeList} element Element to remove from array
     * @return {Object}
     */
    exclude(element) {
        this.element = this.element.filter(item => element !== item)

        return this
    }


    /**
     * Check if element has aria-controls attribute
     *
     * @return {Boolean} Whether element has aria-controls attribute or not
     */
    hasAriaControlsAttr() {
        return this.element.filter(element => element.getAttribute('aria-controls')).length
    }

    // Check if element is in visible viewport (completely in view port)
    isInViewport() {
        this.elementIsInViewport = false
        this.element.forEach((element) => {
            const rect = element.getBoundingClientRect()
            if ((rect.height > 0 || rect.width > 0) &&
                rect.top >= 0 &&
                rect.left >= 0 &&
                rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                rect.right <= (window.innerWidth || document.documentElement.clientWidth)) {

                this.elementIsInViewport = true
            }
        })

        return this.elementIsInViewport
    }


    // Check if element is even partially in viewport
    isPartiallyInViewport() {
        return this.element.some(element => {
            const rect = element.getBoundingClientRect();
            return (
                rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
                rect.bottom > 0 &&
                rect.left < (window.innerWidth || document.documentElement.clientWidth) &&
                rect.right > 0
            );
        });
    }

    /**
     * Get largest element width
     *
     * @return {Integer} Width of largest element
     */
    getWidth(includePadding = true) {
        const widths = this.element.map((element) => {
            const styles = window.getComputedStyle(element)
            let width = parseInt(styles.width, 10)

            if (!includePadding) {
                width = width - parseInt(styles.paddingLeft, 10) - parseInt(styles.paddingRight, 10)
            }

            return width
        })

        return Math.max(widths)
    }


    /**
     * Get largest element height
     *
     * @return {Integer} Height of largest element
     */
    getHeight(includePadding = true) {
        const heights = this.element.map((element) => {
            const styles = window.getComputedStyle(element)
            let height = parseInt(styles.height, 10)

            if (!includePadding) {
                height = height - parseInt(styles.paddingTop, 10) - parseInt(styles.paddingBottom, 10)
            }

            return height
        })

        return Math.max(heights)
    }
}

export default Helpers
